// --- React
import React, {useRef, useState} from 'react';
// --- UI Components
import Input from '@dtci/spellbook/Input';
import Button from '@dtci/spellbook/Button';
import JoditEditor from 'jodit-react';
import {InputLabel} from '../../../../InputLabel/InputLabel';
// --- Redux
import {useSelector} from 'react-redux';
import {useClearanceMemoTitlesActions} from '../../../../../store/clearanceMemoTitles/useClearanceMemoTitles';
// --- API
import {datastageApi} from '../../../../../api';
// --- Utilities
import {usePermissions} from '../../../../../utils/permissions';
import {config} from '../../../../../utils/editorConfig';
// --- Custom Hooks
import {useLoading} from '../../../../../utils/hooks/useLoading';
// --- Dialogs
import {useSaveDialog} from "../../../../dialogs/dialogs/save";
import {useUploadDialog} from "../../../../dialogs/dialogs/upload";
import {startJobStatusTracker} from "../../../../../utils/jobStatusTracker";
import {useToastNotification} from "../../../../ToastNotifications";
import './styles.scss';
import {useDeleteDialog} from "../../../../dialogs/dialogs/delete";

/**
 * EditSectionForm Component
 *
 * This component provides a form for creating/editing clearance memo sections.
 *
 * @component
 * @returns {JSX.Element} A form  for creating/editing a section.
 */
export const EditSectionForm = () => {
    const editor = useRef(null);
    const {section, title} = useSelector((s) => s.titles);
    const [isTitleHidden, setIsTitleHidden] = useState(section?.isTitleHidden);
    const [isVerified, setIsVerified] = useState(section?.isVerified);
    const [isPublic, setIsPublic] = useState(section?.isPublic);
    const {isLegalTVAdmin, isLegalAdmin} = usePermissions();
    const {updateClearanceMemoTitlesFilterForm, updateClearanceMemoTitles} =
        useClearanceMemoTitlesActions();
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {openSaveDialog} = useSaveDialog();
    const {openUploadDialog} = useUploadDialog();
    const {openDeleteDialog} = useDeleteDialog();
    const {openToast} = useToastNotification();

    const toggleState = (currentState, setState, key) => {
        console.log(`Toggling ${key} from`, currentState);
        const newState = !currentState;
        setState(newState);
        updateClearanceMemoTitlesFilterForm({key: 'section', data: {[key]: newState}});
    };

    const handleVerifyPosition = async () => {
        setLoadingTrue();
        try {
            await datastageApi.verifyPosition(section.id);
            const toc = await datastageApi.clearanceMemoTOC(title.id);
            updateClearanceMemoTitles({clearanceMemoTOC: toc});
            toggleState(isVerified, setIsVerified, 'isVerified');
        } catch (error) {
            console.error('Error verifying position:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const handleConvertToSharedSection = () => {
        openSaveDialog('Yes, Convert to Shared Section',
            [`Convert ${section?.title} to a shared section?`], async () => {
                setLoadingTrue();
                try {
                    await datastageApi.convertToSharedSection(section?.id);
                    const toc = await datastageApi.clearanceMemoTOC(title.id);
                    updateClearanceMemoTitles({clearanceMemoTOC: toc, isSectionModalOpen: false});
                } catch (error) {
                    console.error(`Error converting section with ID ${section.id}:`, error);
                } finally {
                    setLoadingFalse();
                }
            });
    };

    const handleConvertToLocalSection = () => {
        openSaveDialog('Yes, Convert to Shared Section',
            [`Are you sure you would like to unlink and create a local version of 
                                ${section?.title} on ${title?.title} (${title.mappedProductId})`], async () => {
                setLoadingTrue();
                try {
                    await datastageApi.convertToLocalSection(section?.id);
                    const toc = await datastageApi.clearanceMemoTOC(title.id);
                    updateClearanceMemoTitles({clearanceMemoTOC: toc, isSectionModalOpen: false});
                } catch (error) {
                    console.error(`Error converting section with ID ${section.id}:`, error);
                } finally {
                    setLoadingFalse();
                }
            });
    };

    const handleUnlinkSection = () => {
        openSaveDialog('Yes, Unlink',
            ['Are you sure you want to remove this shared section from this product?'], async () => {
                setLoadingTrue();
                try {
                    await datastageApi.unlinkClearanceMemoSection(section.id);
                    const toc = await datastageApi.clearanceMemoTOC(title.id);
                    updateClearanceMemoTitles({clearanceMemoTOC: toc, isSectionModalOpen: false});
                } catch (error) {
                    console.error(`Error unlinking section with ID ${section.id}:`, error);
                } finally {
                    setLoadingFalse();
                }
            });
    };

    const onOpenUploadDialog = (data) => {
        openUploadDialog(data, async ({file}) => {
            if (file) {
                const formData = new FormData();
                (Array.isArray(file) ? file : [file]).forEach((f) => formData.append('files', f));
                formData.append('clearanceMemoTOCId', section?.id);
                formData.append('clearanceMemoSectionId', section?.clearanceMemoSectionId);
                try {
                    setLoadingTrue();
                    const res = await datastageApi.uploadClearanceMemoAttachment(formData);

                    startJobStatusTracker(res?.id, async (message, status) => {
                        openToast(message, status);

                        if (status === 'success') {
                            const updatedTitle = await datastageApi.clearanceMemoTitleById(title?.id);
                            const toc = await datastageApi.clearanceMemoTOC(title?.id);
                            updateClearanceMemoTitles({
                                clearanceMemoTOC: toc,
                                title: updatedTitle,
                                section: toc?.find(s => s.id === section?.id)?.clearanceMemoSection
                            });
                        }
                        setLoadingFalse();
                    });

                    setLoadingFalse();

                } catch (error) {
                    console.error('Error uploading attachment:', error);
                    setLoadingFalse();
                }
            }
        });
    };

    const onOpenDeleteDialog = (data) => {
        openDeleteDialog(
            'Yes, Delete',
            [
                `Are you sure you want to delete ${data?.attachmentName}`,
                'Please note: This action can not be reversed.',
            ],
            async () => {
                setLoadingTrue();
                try {
                    const res = await datastageApi.deleteAttachment(data.id, false);

                    if (res?.success === true) {
                        const toc = await datastageApi.clearanceMemoTOC(title?.id);
                        updateClearanceMemoTitles({
                            clearanceMemoTOC: toc,
                            section: toc?.find(s => s.id === section?.id)?.clearanceMemoSection
                        });
                    }

                } catch (error) {
                    console.error('Error removing attachment:', error);
                    openToast(
                        'Error removing attachment',
                        'error',
                    );
                } finally {
                    setLoadingFalse();
                }
            },
        );
    };

    const downloadAttachment = async (rightsLineId, fileName) => {
        setLoadingTrue();
        const res = await datastageApi.downloadAttachment(rightsLineId, fileName);
        saveAs(res.data, fileName);
        setLoadingFalse();
    };

    return (
        <div className="cm-section-editor">
            <div className="button-container">
                <div className="button-group">
                    {!section?.isShared && (
                        <Button
                            iconType="fa-solid fa-wrench"
                            styleType="link"
                            onClick={handleConvertToSharedSection}
                        >
                            Convert to Shared Section
                        </Button>
                    )}
                    {section?.isShared && (
                        <>
                            <Button
                                iconType="fa-solid fa-wrench"
                                styleType="link"
                                onClick={handleConvertToLocalSection}
                            >
                                Convert to Local Section
                            </Button>
                            <Button
                                iconType="fa-solid fa-link-slash"
                                styleType="link"
                                onClick={handleUnlinkSection}
                            >
                                Unlink
                            </Button>
                        </>
                    )}
                </div>
                <div className="button-group">
                    {
                        console.log('isLegalTVAdmin:', isLegalTVAdmin, 'title.productType:', title)
                    }
                    {(!isLegalTVAdmin && title?.productType?.toUpperCase() === 'FEATURE') && (
                        <Button
                            iconType={`fa-solid ${isPublic ? 'fa-circle-x' : 'fa-circle-check'}`}
                            styleType="link"
                            onClick={() => toggleState(isPublic, setIsPublic, 'isPublic')}
                        >
                            Report Only
                        </Button>
                    )}
                    <Button
                        iconType={`fa-solid ${isTitleHidden ? 'fa-eye-slash' : 'fa-eye'}`}
                        styleType="link"
                        onClick={() =>
                            toggleState(isTitleHidden, setIsTitleHidden, 'isTitleHidden')
                        }
                    >
                        {isTitleHidden ? 'Show Title' : 'Hide Title'}
                    </Button>
                    {section?.isShared && !section?.isVerified && (
                        <Button
                            iconType="fa-solid fa-circle-check"
                            styleType="link"
                            onClick={handleVerifyPosition}
                            disabled={!isLegalAdmin}
                        >
                            Verify
                        </Button>
                    )}
                    <Button
                        iconType="fa-solid fa-paperclip"
                        styleType="link"
                        onClick={onOpenUploadDialog}
                    >
                        {section?.attachments.length}
                    </Button>
                </div>
            </div>
            <div className="flexBox">
                <InputLabel label="Name">
                    <Input
                        onChange={(e) =>
                            updateClearanceMemoTitlesFilterForm({
                                key: 'section',
                                data: {title: e.target.value},
                            })
                        }
                        type="text"
                        value={section?.title}
                        width="auto"
                    />
                </InputLabel>
                <JoditEditor
                    ref={editor}
                    value={section?.body}
                    config={config}
                    tabIndex={1}
                    onBlur={(newContent) =>
                        updateClearanceMemoTitlesFilterForm({
                            key: 'section',
                            data: {body: newContent},
                        })
                    }
                />
            </div>
            {section?.attachments && (
                <div className="clearance-memo-attachments"
                     style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                    {section?.attachments.map((doc) => (
                        <div key={doc.id || doc.attachmentName} className="clearance-memo-attachment"
                             style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <a
                                className={"clearance-memo-attachment-link"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    downloadAttachment(doc?.rightslineId, doc?.attachmentName)
                                }}
                            >
                                {doc.attachmentName}
                            </a>
                            <Button iconType="fa-regular fa-trash" sizeType="large" styleType="link" onClick={() => onOpenDeleteDialog(doc)}/>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};